import React, { useEffect, useState } from "react";
import FilterCard, { FilterRemoveButton } from "./FilterCard";
import RamMemoryIcon from "assets/img/svg/ram-memory.svg?react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/Graphs/Popover";
import BrevSlider from "./Slider";
import * as Tooltip from "@radix-ui/react-tooltip";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const marks = [
  {
    value: 0,
    label: "Any",
  },
  {
    value: 8,
    label: "8",
  },
  {
    value: 12,
    label: "12",
  },
  {
    value: 16,
    label: "16",
  },
  {
    value: 24,
    label: "24",
  },
  {
    value: 32,
    label: "32",
  },
  {
    value: 40,
    label: "40",
  },
  {
    value: 48,
    label: "48",
  },
  {
    value: 80,
    label: "80",
  },
  {
    value: 90,
    label: "100",
  },
  {
    value: 100,
    label: "128",
  },
];

interface VRamFilterProps {
  selectedVRam: string;
  setSelectedVRam: (vram: string) => void;
}

const VRamFilter: React.FC<VRamFilterProps> = ({
  selectedVRam,
  setSelectedVRam,
}) => {
  const [selectedSliderValue, setSelectedSliderValue] = useState(() => {
    // Initialize slider value based on initial selectedVRam
    if (selectedVRam === "any") return 0;
    const mark = marks.find((m) => m.label + "GiB" === selectedVRam);
    return mark ? mark.value : 0;
  });

  const onChange = (value: number) => {
    setSelectedSliderValue(value);
    if (value === 0) {
      setSelectedVRam("any");
    } else {
      const trueValue = marks.find((mark) => mark.value === value);
      if (trueValue) {
        setSelectedVRam(trueValue.label + "GiB");
      }
    }
  };

  // Only update slider when selectedVRam changes externally
  useEffect(() => {
    if (selectedVRam === "any") {
      if (selectedSliderValue !== 0) {
        setSelectedSliderValue(0);
      }
    } else {
      const value = selectedVRam.replace("GiB", "");
      const mark = marks.find((m) => m.label === value);
      if (mark && selectedSliderValue !== mark.value) {
        setSelectedSliderValue(mark.value);
      }
    }
  }, [selectedVRam]);

  const title = `${selectedVRam === "any" ? "No minimum " : "At least " + selectedVRam} VRAM`;

  return (
    <Popover>
      <div className="relative inline-flex">
        <PopoverTrigger asChild>
          <FilterCard
            icon={<RamMemoryIcon className="w-4 h-4" />}
            title={title}
            filterApplied={selectedVRam !== "any"}
            hasTrailingAction={selectedVRam !== "any"}
          />
        </PopoverTrigger>
        {selectedVRam !== "any" && (
          <FilterRemoveButton
            filterName="VRAM"
            onRemove={() => setSelectedVRam("any")}
          />
        )}
      </div>
      <PopoverContent className="w-[calc(100vw-2rem)] max-w-[500px] overflow-y-auto p-4">
        <div className="mb-4 flex w-full flex-col">
          <div className="mb-3 flex items-start justify-between gap-3 text-sm font-medium text-gray-700 dark:text-white sm:items-center">
            <div className="flex flex-wrap items-center">
              <h1>Filtering for GPUs</h1>
              {selectedVRam !== "any" ? (
                <>
                  <h1 className="ml-1">with at least</h1>
                  <h1 className="ml-1 font-bold">{selectedVRam}</h1>
                  <h1 className="ml-1">of VRAM</h1>
                </>
              ) : (
                <h1 className="ml-1">with any amount of VRAM</h1>
              )}
              <Tooltip.Provider>
                <Tooltip.Root delayDuration={0}>
                  <Tooltip.Trigger asChild>
                    <InformationCircleIcon className="w-4 h-4 text-gray-400 cursor-help ml-2" />
                  </Tooltip.Trigger>
                  <Tooltip.Portal>
                    <Tooltip.Content
                      side="top"
                      className="z-50 max-w-xs rounded-md bg-zinc-800 px-3 py-2 text-xs text-white shadow-md"
                    >
                      <p className="mb-2">
                        <strong>VRAM (Video RAM)</strong> is the dedicated
                        memory on your GPU.
                      </p>
                      <p>
                        More VRAM allows you to work with larger AI models,
                        higher resolution images, and bigger datasets without
                        running out of memory.
                      </p>
                      <Tooltip.Arrow className="fill-zinc-800" />
                    </Tooltip.Content>
                  </Tooltip.Portal>
                </Tooltip.Root>
              </Tooltip.Provider>
            </div>
            {selectedVRam !== "any" && (
              <button
                onClick={() => setSelectedVRam("any")}
                className="text-sm text-highlight hover:text-highlightDarker font-medium"
              >
                Clear
              </button>
            )}
          </div>
          <div className="flex px-4">
            <BrevSlider
              ariaLabel="Minimum GPU memory"
              getAriaValueText={(value) => {
                const label = marks.find((mark) => mark.value === value)?.label;
                return label === "Any"
                  ? "Any amount of GPU memory"
                  : `${label ?? value} GiB of GPU memory minimum`;
              }}
              step={null}
              marks={marks}
              value={selectedSliderValue}
              onChange={onChange}
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default VRamFilter;
